import React, { useState, useEffect, Fragment } from "react";
import { Icon, Tooltip } from "@scuf/common";
import "./ImageUploadView.scss";
import i18next from "../../../i18next";
import { ReactComponent as ActiveTickMark } from "../../../assets/active-tick-mark.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete-icon.svg";

interface IUploadImageViewProps {
  isLogoUpload?: boolean;
  orgId?: string;
  siteId?: string;
  name?: string;
  title?: string;
  dropTxt?: string;
  previewTxt?: string;
  tooltipTxt: string;
  imgInfo: string;
  onImageDragOrDrop: (file: File) => void;
}

const ImageUploadView = (props: IUploadImageViewProps) => {
  const environmentVariables = (window as any).ENV;

  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [isImageDropping, setIsImageDropping] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>();
  let supportFileTypes = environmentVariables.SITE_IMG_SUPPORTED_TYPE;
  let errorMsg = i18next.t('fileTypeErrorMsg');
  let sizeOfErrorMsg = i18next.t('siteImgSizeOfErrorMsg', {
    width: environmentVariables.SITE_IMG_WIDTH,
    height: environmentVariables.SITE_IMG_HEIGHT,
    maxSize: environmentVariables.MAX_IMG_SIZE_IN_KB
  });

  if (props.isLogoUpload) {
    supportFileTypes = environmentVariables.ORG_IMG_SUPPORTED_TYPE;
    errorMsg = i18next.t('fileTypeErrorMsg');
    sizeOfErrorMsg = i18next.t('orgImgSizeOfErrorMsg', {
      width: environmentVariables.ORG_LOGO_IMG_WIDTH,
      minWidth: environmentVariables.ORG_LOGO_MIN_IMG_WIDTH,
      height: environmentVariables.ORG_LOGO_IMG_HEIGHT,
      minHeight: environmentVariables.ORG_LOGO_MIN_IMG_HEIGHT,
      maxSize: environmentVariables.MAX_IMG_SIZE_IN_KB
    });
  }

  const updateErrorMessage = (errorMsg: string) => {
    setCurrentFile(null);
    setErrorMessage(errorMsg);
    props.onImageDragOrDrop(null!);
  }

  const handleDropFile = (dragEvent: React.DragEvent<HTMLDivElement>) => {
    dragEvent.preventDefault();
    const file = dragEvent.dataTransfer.items[0].getAsFile();
    setIsImageDropping(false);
    setErrorMessage(null);
    if (file && supportFileTypes.includes(file.type)) {
      updateImgAction(file);
    } else {
      updateErrorMessage(errorMsg);
    }
  };

  const checkImgHeight = (file: any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (upload: any) => {
        const img = new Image();
        img.onload = () => {
          resolve({
            height: img.height,
            width: img.width,
            size: upload.total
          });
        };
        img.src = upload.target.result;
      };
      reader.readAsDataURL(file);
    })
  };

  const updateImgAction = (files: any) => {
    checkImgHeight(files)
      .then((obj: any) => {
        if (
          (props.isLogoUpload &&
            (obj.height > environmentVariables.ORG_LOGO_IMG_HEIGHT ||
              obj.height < environmentVariables.ORG_LOGO_MIN_IMG_HEIGHT ||
              obj.width > environmentVariables.ORG_LOGO_IMG_WIDTH ||
              obj.width < environmentVariables.ORG_LOGO_MIN_IMG_WIDTH ||
              obj.size > (environmentVariables.MAX_IMG_SIZE_IN_KB * 1000)
            )) ||
          (!props.isLogoUpload &&
            (obj.height < environmentVariables.SITE_IMG_HEIGHT ||
              obj.width < environmentVariables.SITE_IMG_WIDTH)) ||
          obj.size > (environmentVariables.MAX_IMG_SIZE_IN_KB * 1000)
        ) {
          updateErrorMessage(sizeOfErrorMsg);
        } else {
          setCurrentFile(files);
          props.onImageDragOrDrop(files);
        }
      })
      .catch((error: any) => {
        console.log(error);
      });
  }

  const handleBrowseFileSelect = (
    changeEvent: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = changeEvent.currentTarget.files;
    setIsImageDropping(false);
    setErrorMessage(null);
    if (files?.length && supportFileTypes.includes(files[0].type)) {
      updateImgAction(files[0]);
    } else {
      setErrorMessage(errorMsg);
    }
  };

  const deleteImage = () => {
    if (currentFile === null) {
      return;
    }
    setCurrentFile(null);
    props.onImageDragOrDrop(null!);
  };

  const getImageSizeInUnit = () => {
    if (currentFile) {
      const fileSizeInBytes = currentFile?.size;
      const fileSizeInKb = fileSizeInBytes / 1024;
      return fileSizeInKb > 1024 ? (fileSizeInKb / 1024).toFixed(2) + ' MB' : fileSizeInKb.toFixed(2) + ' KB'
    }
  };

  return (

    <div className="image-upload-view">
      <div className="title-txt">{props.title}</div>
      <div
        className={`drop-section ${errorMessage && !isImageDropping
          ? "error"
          : currentFile != null
            ? "active"
            : isImageDropping
              ? "dropping"
              : ""
          }`}
        onDragOver={(e) => {
          setIsImageDropping(true);
          e.stopPropagation();
          e.preventDefault();
        }}
        onDragLeave={(e) => {
          setIsImageDropping(false);
          e.stopPropagation();
          e.preventDefault();
        }}
        onDrop={handleDropFile}
      >
        <div className="drag-drop-txt">{props.dropTxt}</div>
        <div className="img-info">({props.imgInfo})</div>
        <div className="or-text">{i18next.t('orTxt')}</div>
        <div className="file-upload-input-box">
          <input
            type="file"
            id="browseFile"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleBrowseFileSelect(e);
            }}
            value={undefined}
          />
          <label className="browse-file-label" htmlFor="browseFile">
            {i18next.t('browseFile')}
          </label>
        </div>
        {currentFile != null && !errorMessage && (
          <Fragment>
            <ActiveTickMark />
            <div className="file-name-txt">
              <label className="browse-file-name">
                {currentFile?.name} ({getImageSizeInUnit()})
              </label>
              <DeleteIcon
                onClick={() => deleteImage()}
                className="delete-icon"
              />
            </div>
          </Fragment>
        )}
        {errorMessage && <div className="error-text">{errorMessage}</div>}
      </div>
      <div className="img-preview">
        <div className="img-preview-header">
          <div
            className="preview-txt"
          >
            {props.previewTxt}
          </div>
          <div className="tooltip-wrapper">
            <Tooltip
              element={
                <Icon
                  className="title-tooltip-icon"
                  root="global"
                  name="badge-info"
                  size="medium"
                />
              }
              content={props.tooltipTxt}
              size="mini"
              position="right center"
            />
          </div>

        </div>
        <div className={`img-preview-container ${props.isLogoUpload === false ? 'preview-container' : ''}`}>
          <div className="img-preview-wrapper">
            {currentFile != null && <img className="img-preview-box" src={URL.createObjectURL(currentFile)} />}

          </div>
          <div className="img-preview-delete-icon">
            <DeleteIcon
              onClick={() => deleteImage()}
              className={`delete-icon ${currentFile != null ? 'active' : "in-active"}`}
            />
          </div>
        </div>
      </div>
    </div>

  );
};

ImageUploadView.defaultProps = {
  isLogoUpload: false,
};

export default ImageUploadView;
